import { getEnv, isDevelopment, isProduction } from "../utils/runtimeEnv";
/**
 * API URL constants resolved per environment (production, development, local, preview).
 *
 * Exports {@link ApiBaseUrls} for service base URLs, {@link API_ENDPOINTS} for
 * fully-qualified endpoint paths, and {@link RPA_PATHS_ONLY_FOR_QUEUE} for
 * RPA task queue relative paths.
 *
 * When {@link REACT_APP_GATEWAY_URL} is defined (including empty string), service
 * bases use that gateway for same-origin BFF (GKE nginx). Unset keeps classic
 * production / development / local / preview resolution so Firebase Hosting and
 * `npm run start:dev` still call `apis-dev` / `apis`.
 * @module apiUrls
 */

/**
 * True when the build set REACT_APP_GATEWAY_URL (empty string counts).
 * Used by the GKE medonc nginx BFF image for same-origin API calls.
 */
const USE_GATEWAY_OVERRIDE = getEnv("REACT_APP_GATEWAY_URL") !== undefined;

/** Gateway URL resolved from REACT_APP_ENV, or REACT_APP_GATEWAY_URL when set. */
const GATEWAY_URL = USE_GATEWAY_OVERRIDE
  ? getEnv("REACT_APP_GATEWAY_URL") || ""
  : isProduction()
    ? "https://apis.risalabs.ai"
    : getEnv("REACT_APP_ENV") === "local"
      ? "http://localhost:8080"
      : getEnv("REACT_APP_ENV") === "preview"
        ? getEnv("REACT_APP_GATEWAY_URL") || ""
        : "https://apis-dev.risalabs.ai";

/**
 * Resolve a service base URL for prod / development / gateway fallback.
 *
 * @param segment - Path segment under the gateway (e.g. ``backend-service``), or
 *   empty for the gateway root.
 * @param productionUrl - Absolute URL used when REACT_APP_ENV is production.
 * @param developmentUrl - Absolute URL used when REACT_APP_ENV is development
 *   and no gateway override is set.
 * @returns Trailing-slash base URL for the service.
 */
const resolveServiceBase = (
  segment: string,
  productionUrl: string,
  developmentUrl: string,
): string => {
  if (USE_GATEWAY_OVERRIDE) {
    return segment === "" ? `${GATEWAY_URL}/` : `${GATEWAY_URL}/${segment}/`;
  }
  if (isProduction()) {
    return productionUrl;
  }
  if (isDevelopment()) {
    return developmentUrl;
  }
  return segment === "" ? `${GATEWAY_URL}/` : `${GATEWAY_URL}/${segment}/`;
};

const RPA_SERVICE_CLOUDRUN = resolveServiceBase(
  "rpa-service/v1",
  "https://api.risalabs.ai/rpa-service/v1/",
  "https://api-dev.risalabs.ai/rpa-service/v1/",
);

// Dev uses coverage-service; local/preview historically used backend-service/medical.
const EV_BV_SERVICE = USE_GATEWAY_OVERRIDE
  ? `${GATEWAY_URL}/coverage-service/`
  : isProduction()
    ? "https://apis.risalabs.ai/backend-service/medical/"
    : isDevelopment()
      ? "https://apis-dev.risalabs.ai/coverage-service/"
      : `${GATEWAY_URL}/backend-service/medical/`;

/**
 * Base URL for the dedicated coverage-service across all environments.
 *
 * The legacy backend-service coverage logic is deprecated and returns stale
 * values, so coverage calls must target coverage-service directly.
 */
const COVERAGE_SERVICE = resolveServiceBase(
  "coverage-service",
  "https://apis.risalabs.ai/coverage-service/",
  "https://apis-dev.risalabs.ai/coverage-service/",
);

const PA_ORDER_CREATION_SERVICE = resolveServiceBase(
  "pa-order-creation",
  "https://apis.risalabs.ai/pa-order-creation/",
  "https://apis-dev.risalabs.ai/pa-order-creation/",
);

/** Base URL for pharmacy-pa-order-creation (distinct from medical ``pa-order-creation``). */
const PHARMACY_PA_ORDER_CREATION_SERVICE = resolveServiceBase(
  "pharmacy-pa-order-creation",
  "https://apis.risalabs.ai/pharmacy-pa-order-creation/",
  "https://apis-dev.risalabs.ai/pharmacy-pa-order-creation/",
);

const PHARMACY_RPA_SERVICE = resolveServiceBase(
  "pharmacy-rpa-service",
  "https://apis.risalabs.ai/pharmacy-rpa-service/",
  "https://apis-dev.risalabs.ai/pharmacy-rpa-service/",
);

const BACKEND_SERVICE = resolveServiceBase(
  "backend-service",
  "https://apis.risalabs.ai/backend-service/",
  "https://apis-dev.risalabs.ai/backend-service/",
);

const BASE_URL = resolveServiceBase(
  "",
  "https://apis.risalabs.ai/",
  "https://apis-dev.risalabs.ai/",
);

const AI_SERVICE = resolveServiceBase(
  "ai-service",
  "https://apis.risalabs.ai/ai-service/",
  "https://apis-dev.risalabs.ai/ai-service/",
);

const AUTH_URL = USE_GATEWAY_OVERRIDE
  ? `${GATEWAY_URL}/api/v1/user-auth`
  : isProduction() || isDevelopment()
    ? "https://authentication.risalabs.ai/api/v1/user-auth"
    : `${GATEWAY_URL}/api/v1/user-auth`;

const PA_ORDER_LEGACY_SUBMISSION_SERVICE_URL = resolveServiceBase(
  "pa-order-creation",
  "https://api.risalabs.ai/pa-order-creation/",
  "https://api-dev.risalabs.ai/pa-order-creation/",
);

const RPA_SERVICE_ONE_RISA = resolveServiceBase(
  "rpa-service",
  "https://apis.risalabs.ai/rpa-service/",
  "https://apis-dev.risalabs.ai/rpa-service/",
);

const PA_SUBMISSION_SERVICE = resolveServiceBase(
  "pa-submission",
  "https://apis.risalabs.ai/pa-submission/",
  "https://apis-dev.risalabs.ai/pa-submission/",
);

/** Base URLs for each backend service, resolved per environment. */
export const ApiBaseUrls = {
  AI_SERVICE: AI_SERVICE,
  AUTH_URL: AUTH_URL,
  COVERAGE_SERVICE: COVERAGE_SERVICE,
  EV_BV_SERVICE: EV_BV_SERVICE,
  LOCAL_HOST: "http://127.0.0.1:8000/rpa-service/",
  RPA_SERVICE_CLOUDRUN: RPA_SERVICE_CLOUDRUN,
  PA_ORDER_CREATION_SERVICE: PA_ORDER_CREATION_SERVICE,
  PA_ORDER_LEGACY_SUBMISSION_SERVICE: PA_ORDER_LEGACY_SUBMISSION_SERVICE_URL,
  PHARMACY_PA_ORDER_CREATION_SERVICE: PHARMACY_PA_ORDER_CREATION_SERVICE,
  PHARMACY_RPA_SERVICE: PHARMACY_RPA_SERVICE,
  BACKEND_SERVICE: BACKEND_SERVICE,
  BASE_URL: BASE_URL,
  DEV_SUBMI_PA_FORM:
    "https://apis.risalabs.ai/prior-auth-submission/api/v1/pa-submission/submit-pa-form",
  PROD_SUBMI_PA_FORM:
    "https://apis.risalabs.ai/prior-auth-submission/api/v1/pa-submission/submit-pa-form",
  PROXY_BASE_URL:
    "https://us-central1-prior--backen-prod-svc-u4g8.cloudfunctions.net/http-proxy",
  RPA_SERVICE_ONE_RISA: RPA_SERVICE_ONE_RISA,
  PA_SUBMISSION_SERVICE: PA_SUBMISSION_SERVICE,
} as const;

/** Fully-qualified API endpoint URLs used throughout the dashboard. */
export const API_ENDPOINTS = {
  ADD_EDIT_GI_CONFIGURATION: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/group-inbox/push-config`,
  ADD_EDIT_PORTAL_CREDENTIAL: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}commons/portal/add-credential`,
  ALGOLIA_FACET_COUNTS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/algolia-facet-counts`,
  ALGOLIA_MULTI_FACET_COUNTS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/algolia-multi-facet-counts`,
  ALGOLIA_SEARCH: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/algolia-search`,
  APPEND_ROW_TO_GSHEET: `${ApiBaseUrls.BACKEND_SERVICE}commons/gsheet/append-data-to-specified-sheet`,
  AUDIT_TRIAL_OVERVIEW: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/get-audit-trail-overview`,
  AUTH_LETTER_REVIEW_DUMP: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-on-file/submit-auth-letter-entry`,
  // Real extraction test endpoint. Uses the env-based gateway (apis-dev in dev,
  // apis in prod) so browser CORS matches the running origin.
  EXTRACT_AUTH_LETTER_UPLOAD: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-on-file/extract-auth-letter-upload`,
  AUTH_ON_FILE_CHECK: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/auth-check`,
  AUTH_STATUS_CHANGE_COMMENTS: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/insert/bulk/auth-status-comments`,
  GET_AUTH_LETTER_AGENT_DIFF: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-on-file/get-diff-from-agent`,
  UPDATE_ORDER_REVIEW_STATUS: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/order-review-status`,
  AUTH_TOKEN: `${ApiBaseUrls.BACKEND_SERVICE}auth/token`,
  BEARER_TOKEN_URL: `${ApiBaseUrls.AUTH_URL}/token`,
  CHECK_AVAILITY_PAYER_ID: `${ApiBaseUrls.BACKEND_SERVICE}commons/availity/check-availity-payer-id`,
  CHECK_CLAIMS_FOR_CSV: `${ApiBaseUrls.BACKEND_SERVICE}commons/claims/check-claims-for-csv`,
  CHECK_RATE_LIMIT_PASSWORD_RESET: `${ApiBaseUrls.AUTH_URL}/check-rate-limit-password-reset`,
  COMMENT_RPA_MODAL_SAVE: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/mark-as-completed`,
  COMPRESS_PDF: `${ApiBaseUrls.BACKEND_SERVICE}commons/pdf-operations/compress-pdf`,
  COPY_DOCUMENTS_BY_PATH: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/copy-documents-by-path`,
  CREATE_PORTAL_SECRET: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}commons/portal/create-secret`,
  DELETE_CMM_ORDER: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}delete-entry-on-cmm`,
  DETERMINE_CLINICAL_QUESTIONNAIRE_LLM: `${ApiBaseUrls.BASE_URL}ai-service/commons/medical-necessity/get-medical-necessity-llm`,
  DETERMINE_ICD_CODE: `${ApiBaseUrls.BASE_URL}medical-necessity/v1/nycbs/determine-icd-code-llm`,
  DETERMINE_ICD_CODE_LLM: `${ApiBaseUrls.BASE_URL}ai-service/commons/icd-code-detect/determine-icd-code-llm`,
  DOWNLOAD_AUDIT_TRAIL: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}utility_apis/download-audit-trail`,
  DUMP_LATEST_MEDICAL_PA_FORM_DATA: `${ApiBaseUrls.PA_SUBMISSION_SERVICE}medical/submission-medical-pa/form-data/latest`,
  EDIT_COVER_MY_MEDS_REQUEST: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}edit-cover-my-meds_request`,
  EV_BV_FROM_EXCEL: `${ApiBaseUrls.COVERAGE_SERVICE}coverage/check-coverage-for-csv`,
  FETCH_AUTH_GRID: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/show-auth-grid`,
  FETCH_NCCN_GUIDELINES: `${ApiBaseUrls.AI_SERVICE}medical/guidelines/fetch-guidelines`,
  FETCH_NCCN_GUIDELINES_FOR_ORDER: `${ApiBaseUrls.AI_SERVICE}medical/guidelines/fetch-nccn-guidelines`,
  FETCH_NCCN_GUIDELINES_DIRECT: `${ApiBaseUrls.AI_SERVICE}medical/guidelines/fetch-nccn-guidelines-direct`,
  SUBMIT_NCCN_EVALUATION: `${ApiBaseUrls.AI_SERVICE}medical/guidelines/submit-nccn-evaluation`,
  RECOMPUTE_NCCN_EVALUATION: `${ApiBaseUrls.AI_SERVICE}medical/guidelines/recompute-nccn-evaluation`,
  GET_NCCN_EVALUATION: `${ApiBaseUrls.AI_SERVICE}medical/guidelines/get-nccn-evaluation`,
  FETCH_PORTAL_PASSWORD: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}commons/portal/fetch-password`,
  GET_ALL_DIAGNOSIS_CODES_FOR_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-diagnosis-details`,
  GET_ALL_DOCUMENTS_FOR_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}documents`,
  GET_ALL_INSURANCE_DATA_FOR_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-insurance-details`,
  GET_ALL_QUESTIONS_FOR_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}questionnaire-data`,
  GET_ASSIGNED_ORDERS: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}nycbs_bg_query/get-assigned-cmm-orders-with-status`,
  GET_AUDIT_TRIAL_DETAILS: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/get-detailed-audit-trail`,
  GET_AUTH_GRID_SOURCE: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/get-auth-grid-source`,
  GET_DX_CODE_MAP_NCCN: `${ApiBaseUrls.EV_BV_SERVICE}icdcode/get-dxcodemap-nccn`,
  GET_EV_BV_REPORT_DETAILS: `${ApiBaseUrls.COVERAGE_SERVICE}coverage/prepare-coverage-excl`,
  GET_FACETS_COUNT_FROM_POSTGRES: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/facet-counts`,
  GET_FAX_STATUS: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/get-fax-status`,
  GET_FILE_FROM_NYCBS_SFTP: `${ApiBaseUrls.PHARMACY_PA_ORDER_CREATION_SERVICE}nycbs/get-file-from-nycbs-sftp`,
  GET_FILENAME_SUMMARY: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/filename-summary`,
  GET_FINAL_WORKLIST_DATA: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/external-worklist/get-data`,
  GET_FORM_NAME: `${ApiBaseUrls.BACKEND_SERVICE}medical/priority-health/get-form-name`,
  /** Latest Group Inbox configurations for every eligible view tab. */
  GROUP_INBOX_CONFIGS: `${ApiBaseUrls.BACKEND_SERVICE}commons/configs/group-inbox`,
  GET_INTERNAL_PROCESSED_ORDER_STATUS: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-processed-pa-orders`,
  GET_LATEST_MEDICAL_PA_FORM_DATA: `${ApiBaseUrls.PA_SUBMISSION_SERVICE}medical/submission-medical-pa/form-data/latest`,
  GET_MEDICAL_PA_ANALYTICS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/external-worklist/get-analytics`,
  GET_PATIENT_ELIGIBILITY_DETAILS: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-patient-eligibility-details`,
  GET_PHARMA_STP_FILE_DATA: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/get-data-from-bigquery`,
  GET_PRESCRIPTION_DATA: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}prescription-data`,
  GET_SINGLE_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-single-order-details`,
  GET_UNASSIGNED_ORDERS: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}nycbs_bg_query/get-unassigned-cmm-orders`,
  GET_UNIQUE_CMM_ORDERS: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-unique-cmm-orders`,
  INGEST_PDF_CSV_NAR_GRID: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/ingest-pdf-csv-nar-grid`,
  KILL_SWITCH: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/kill-switch`,
  LIST_HISTORIC_NAR: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/list-historic-nar-recommendations`,
  INGEST_HISTORIC_NAR: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/ingest-historic-nar-recommendation`,
  /** Admin transition out of the Historic NAR Needs Review lane. */
  CLEAR_HISTORIC_NAR_LANE: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/clear-nar-lane`,
  LOG_EVENT_ENDPOINT: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/log-frontend-event`,
  MEDICAL_PA_SUBMISSION: `${ApiBaseUrls.PA_SUBMISSION_SERVICE}medical/submission-medical-pa/submit-medical-pa-order`,
  MEMBER_BASE: `${ApiBaseUrls.BACKEND_SERVICE}commons/rbac/members`,
  MEMBER_BULK_UPDATE: `${ApiBaseUrls.BACKEND_SERVICE}commons/rbac/members/bulk-update`,
  MEMBER_INVITE: `${ApiBaseUrls.BACKEND_SERVICE}commons/rbac/members/invite`,
  MERGE_PDFS: `${ApiBaseUrls.BACKEND_SERVICE}commons/pdf-operations/merge-pdfs`,
  NAR_RPA_TRIGGER: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/nar/availity-healthfirst-checknar`,
  ONCOEMR_TASKS: `${ApiBaseUrls.BACKEND_SERVICE}oncoemr-tasks`,
  ONCOEMR_TASKS_USERS_SEARCH: `${ApiBaseUrls.BACKEND_SERVICE}oncoemr-tasks/users/search`,
  ORDER_REGIMEN_ICD: `${ApiBaseUrls.BACKEND_SERVICE}medical/order/regimen-icd`,
  ORDERS_BULK_UPDATE: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/update/bulk/order`,
  POST_STATUS_TRACKING: `${ApiBaseUrls.BASE_URL}ai-service/pharmacy/astera/push-orders`,
  PREPARE_CLAIMS_EXCEL: `${ApiBaseUrls.BACKEND_SERVICE}commons/claims/prepare-claims-excl`,
  PREPARE_FORM_QUESTIONNAIRE: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/prepare-form-questionnaire`,
  PROCESS_CSV_PHARMACY_PA: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/external-worklist/process-csv`,
  PUSH_FILES_TO_SFTP: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/nycbs/push-files-to-sftp`,
  RE_RUN_ONCO_EMR_WORKFLOW: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}submit-pa`,
  REFETCH_CLINICAL_ATTACHMENT: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}commons/clinical-docs`,
  REFRESH_MEDICAL_PA_ORDERS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/core-medical-pa/start-medical-pa`,
  REFRESH_TEMPLATES: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/refresh-templates`,
  RPA_TASK_QUEUE: `${ApiBaseUrls.BACKEND_SERVICE}commons/queue/rpa`,
  NEW_CENTURY_HEALTH_QUESTIONNAIRE: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/pa-submission/new-century-health/questionnaire/fetch`,
  NEW_CENTURY_HEALTH_QUESTIONNAIRE_UPDATE: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/pa-submission/new-century-health/questionnaire`,
  LEGACY_MEDICAL_PA_SUBMISSION_URL: `${ApiBaseUrls.PA_ORDER_LEGACY_SUBMISSION_SERVICE}v1/medical-pa/submit-medical-pa-order`,
  RUN_STATUS_TRACKING: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}run_status_tracking`,
  SEARCH_FOR_MEDICAL_PA_ORDERS_FROM_POSTGRES: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/search`,
  EXPORT_FOR_CLIENT: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/export-for-client`,
  SEARCH_IN_CMM_INPUT_TABLE: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}search-in-cmm-table`,
  SEND_FAX_FROM_CONFIG: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/send-fax-from-config`,
  SEND_INVITE_VIA_EMAIL: `${ApiBaseUrls.AUTH_URL}/invite-user-to-dashboard`,
  SEND_TO_PLAN_IN_CMM: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}send-to-plan`,
  SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query`,
  SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES_INSERT: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/insert`,
  SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES_UPDATE: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/update`,
  SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES_UPSERT: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/upsert`,
  SUBMIT_FAX_FORM_QUESTIONNAIRE: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/submit`,
  TEMPLATE_VALIDATOR_VALIDATE: `${ApiBaseUrls.AI_SERVICE}medical/template-validator/validate-templates-batch`,
  TEST_LOCATION_DETAILS: `${ApiBaseUrls.BACKEND_SERVICE}medical/test-location-details/get`,
  TEST_LOCATION_DETAILS_SEARCH: `${ApiBaseUrls.BACKEND_SERVICE}medical/test-location-details/search`,
  TEST_LOCATION_DETAILS_UPSERT: `${ApiBaseUrls.BACKEND_SERVICE}medical/test-location-details/upsert`,
  UPDATE_BIGQUERY_DATA: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/update-data-to-bigquery`,
  UPDATE_CIG_BOX_DATA: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/auth-check/write-back-oncoemr`,
  UPDATE_CMM_TABLE: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}nycbs_bg_query/update-cmm-table`,
  UPDATE_J_CODE_DOCUMENTS: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/update-j-code-documents`,
  FAX_LETTER_ORDER: `${ApiBaseUrls.BACKEND_SERVICE}medical/fax-letter/order`,
  FAX_LETTER_DETAILS: `${ApiBaseUrls.BACKEND_SERVICE}medical/fax-letter/details`,
  FAX_LETTER_REVIEW: `${ApiBaseUrls.BACKEND_SERVICE}medical/fax-letter/review`,
  FAX_LETTER_MARK_INCORRECT: `${ApiBaseUrls.BACKEND_SERVICE}medical/fax-letter/mark-incorrect`,
  UPDATE_J_CODES: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/update-j-codes`,
  UPDATE_PROFILE: `${ApiBaseUrls.BACKEND_SERVICE}commons/profile/update`,
  UPDATE_QUESTIONAIRE_RESPONSE: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}edit-cover-my-meds-questionnaire`,
  UPDATE_REPORTS: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/update-reports`,
  UPLOAD_AUTH_LETTER: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}commons/upload-authorization-letter`,
  VIEW_ALL_AUDIT_TRAIL: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/get-audit-trail`,
  CANDID_FILLING_RPA_TRIGGER: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/candid-filling`,
  DELETE_J_CODE: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/delete-j-code`,
  ADD_J_CODE: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/add-j-code`,
  DENIAL_QUERY_BUCKET_UPDATE: `${ApiBaseUrls.BACKEND_SERVICE}medical/denial-query-bucket/upsert`,
  DENIAL_QUERY_BUCKET_GET: `${ApiBaseUrls.BACKEND_SERVICE}medical/denial-query-bucket/fetch`,
  /** Candid payer-directory lookup for auth-letter payer search. */
  CANDID_PAYERS: `${ApiBaseUrls.COVERAGE_SERVICE}coverage/candid/payers`,
  /** Candid authorization write-back for auth-letter upload. */
  CANDID_AUTHORIZATIONS: `${ApiBaseUrls.COVERAGE_SERVICE}coverage/candid/authorizations`,
  /** Candid document upload for auth-letter upload. */
  CANDID_DOCUMENT_UPLOAD: `${ApiBaseUrls.COVERAGE_SERVICE}coverage/candid/documents`,
  /** Candid notes write-back for an order. */
  CANDID_NOTES_UPLOAD: `${ApiBaseUrls.COVERAGE_SERVICE}coverage/candid/notes`,
  /** Candid writeback history for an order. */
  CANDID_WRITEBACK_HISTORY: `${ApiBaseUrls.COVERAGE_SERVICE}coverage/candid/writeback/history`,
  /** Backend proxy: whether PMG already has an mrn_state row (`$mrn` + `org_id`). */
  PATIENT_GRAPH_INGESTION_STATUS: `${ApiBaseUrls.BACKEND_SERVICE}medical/patient-graph/patients/$mrn/exists`,
  /** Backend proxy: ingest a medical PA order into the patient medical graph. */
  INGEST_IN_PATIENT_GRAPH: `${ApiBaseUrls.BACKEND_SERVICE}medical/patient-graph/ingest-pa-order`,
  QUICK_REVIEW_LIST: `${ApiBaseUrls.BACKEND_SERVICE}urology/quick-review/list`,
  QUICK_REVIEW_COUNT: `${ApiBaseUrls.BACKEND_SERVICE}urology/quick-review/count`,
  QUICK_REVIEW_DETAIL: `${ApiBaseUrls.BACKEND_SERVICE}urology/quick-review/detail`,
  QUICK_REVIEW_DECISION: `${ApiBaseUrls.BACKEND_SERVICE}urology/quick-review/decision`,
  KILL_SWITCH_BULK_TOGGLE: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/kill-switch/bulk`,
  ALL_J_CODES_BY_PAYER: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/show-auth-grid/j-codes`,
  ORDERS_BULK_ASSIGN: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/update/bulk/assignee`,
};
/** Relative RPA path segments used exclusively for the RPA task queue. */
export const RPA_PATHS_ONLY_FOR_QUEUE = {
  AUTH_ON_FILE_CHECK: `medical/auth-check/`,
  NAR_RPA_TRIGGER: `medical/nar/availity-healthfirst-checknar`,
  UPLOAD_AUTH_LETTER: `commons/upload-authorization-letter`,
  WRITE_BACK_COMMENTS: `medical/auth-check/write-back-oncoemr`,
  CLINICAL_DOCS: `commons/clinical-docs`,
} as const;
